/**
 * Platform detection helpers.
 *
 * The same bundle serves web and the Capacitor native shells (iOS/Android).
 * Use these helpers to branch into native-only code paths (background GPS,
 * push, native camera, secure storage, biometrics). On web every helper returns
 * a safe falsy/`'web'` value, so importing this module never breaks the web app.
 *
 * @capacitor/core is a hard dependency of the mobile build but is also safe to
 * import on web (it ships a web no-op implementation). We still guard every call
 * defensively so a missing/older plugin can never throw at runtime.
 */
import { Capacitor } from '@capacitor/core'

/**
 * True when running inside a native Capacitor shell (iOS/Android).
 * @returns {boolean}
 */
export function isNative() {
  try {
    return Capacitor.isNativePlatform()
  } catch {
    return false
  }
}

/** Alias kept for readability at call sites. */
export function isCapacitor() {
  return isNative()
}

/**
 * Current platform: 'ios' | 'android' | 'web'.
 * @returns {string}
 */
export function getPlatform() {
  try {
    return Capacitor.getPlatform()
  } catch {
    return 'web'
  }
}

export function isIOS() {
  return getPlatform() === 'ios'
}

export function isAndroid() {
  return getPlatform() === 'android'
}

/**
 * Whether a given Capacitor plugin is available in this build/runtime.
 * @param {string} name plugin name (e.g. 'PushNotifications')
 * @returns {boolean}
 */
export function isPluginAvailable(name) {
  try {
    return Capacitor.isPluginAvailable(name)
  } catch {
    return false
  }
}
